import { createContext, useContext, useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

/**
 * The split-screen side (left or right page) a component lives in, provided only while split screen
 * is open on desktop. The desktop job screen covers just that side instead of the whole window, so
 * the other page, the menu and the top bar stay visible.
 */
export const SplitScreenAreaContext = createContext<RefObject<HTMLDivElement> | null>(null);

/**
 * Fixed-position bounds (top/left/right/bottom) of the split-screen side this component lives in,
 * tracking that side's size; undefined outside split screen. Spread onto a `fixed` container's style
 * to cover only that side.
 */
export function useSplitScreenBounds(): CSSProperties | undefined {
    const splitArea = useContext(SplitScreenAreaContext);
    const [bounds, setBounds] = useState<CSSProperties | undefined>(undefined);
    // Layout effect: measure before the first paint, or the overlay flashes full-window for a frame.
    useLayoutEffect(() => {
        const el = splitArea?.current;
        if (!el) {
            setBounds(undefined);
            return;
        }
        const measure = () => {
            const r = el.getBoundingClientRect();
            setBounds({ top: r.top, left: r.left, right: window.innerWidth - r.right, bottom: window.innerHeight - r.bottom });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [splitArea]);
    return bounds;
}

/**
 * True inside split screen's right page. The left page owns the address bar, so the right page must
 * not read deep-link params from it, write to it, or react to its navigation events.
 */
export const SecondaryPaneContext = createContext(false);
export const useIsSecondaryPane = () => useContext(SecondaryPaneContext);
